import { useEffect, useState, useCallback } from "react";
import useElementList, { FormEntity } from "hooks/FormDesigner/useElementList";
import ElementList from "components/FormDesigner/ElementList";
import { CommonPropertyProps } from "schemas/elements";
import { useTranslation } from "react-i18next";
import {
	Elem,
	ElemBefore,
	SearchSelectOptionProps,
} from "@alm/flow-ds/components";
import { AlertProps } from "@alm/flow-ds/components";
import { status } from "../../../../constants";
import api from "lib/api";
import axios from "axios";

type Props = Omit<CommonPropertyProps, "value"> & {
	value: [];
	formEntity: FormEntity;
	legacySearchField?: SearchSelectOptionProps;
	searchUri?: SearchSelectOptionProps;
	hideMultiInputText?: boolean;
	addAlert?: (alert: string, status: AlertProps["status"]) => void;
	tableNested?: boolean;
};

export default ({
	updateStore,
	field,
	required,
	value,
	desc,
	validation,
	elementIndex,
	formEntity,
	addAlert,
	isEmu,
	legacySearchField,
	nestedField,
	searchUri,
	hideMultiInputText,
	tableNested,
}: Props) => {
	const { t } = useTranslation();
	const [init, setInit] = useState(false);

	const {
		state,
		addElement,
		moveElement,
		removeElement,
		updateElement,
		updateValidation,
		updateElementValidation,
		initNestedElements,
		updateEntityData,
	} = useElementList();

	useEffect(() => {
		// Gets initial values for the nested elements on render
		initNestedElements({
			nestedElements: value,
			nestedValidation: validation,
			nestedFormEntity: formEntity,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (init)
			updateStore({
				elementIndex,
				property: field,
				value: state.elements,
				nestedField,
			});
		else setInit(true);
	}, [state, elementIndex, field, updateStore, init, nestedField]);

	// Get entity data using search request uri
	const getEntityData = useCallback(async () => {
		if (searchUri !== undefined && searchUri !== null) {
			try {
				const data = await api.entity.getEntityData(searchUri.label);
				updateEntityData(data);
			} catch (error) {
				if (addAlert) addAlert("errorLoadingFormList", status.ERROR);
				if (error && axios.isAxiosError(error))
					console.error(`Error: ${error.message}`);
			}
		} else {
			updateEntityData([]);
		}
	}, [searchUri, addAlert, updateEntityData]);

	useEffect(() => {
		if (nestedField === "search") {
			getEntityData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchUri]);

	return (
		<Elem className="elem-elements">
			<ElemBefore
				label={
					nestedField === "search"
						? t("formDesigner.labels.viewElements")
						: t("formDesigner.labels.nestedElements")
				}
				required={required}
			/>
			<div className="hint-text">
				{t(`formDesigner.elements.${desc}`)}
			</div>
			<ElementList
				isEmu={isEmu}
				addAlert={addAlert}
				isNested={true}
				nestedField={nestedField}
				state={state}
				validation={validation}
				addElement={addElement}
				moveElement={moveElement}
				removeElement={removeElement}
				updateElement={updateElement}
				updateValidation={updateValidation}
				updateElementValidation={updateElementValidation}
				legacySearchField={legacySearchField}
				isSearchView={nestedField === "search" ? true : undefined}
				hideMultiInputText={hideMultiInputText}
				tableNested={tableNested}
			/>
		</Elem>
	);
};
